import React, { useEffect, useState } from 'react';
import { safeHref } from '../../../../utils/common/safe-url';
import toast from 'react-hot-toast';
import {
  AdCampaign,
  adminListAdCampaigns,
  adminReviewAdCampaign,
  ReportedAd,
  getReportedAds,
  resolveAdReport,
} from '../../../../api/ads/ads.api';
import {
  AdAccount,
  adminListBusinessAccounts,
  adminReviewBusinessAccount,
  adminSetAdAccountCredit,
  TopUp,
  adminListPendingTopUps,
  adminReviewTopUp,
} from '../../../../api/ads/ad-account.api';
import AdminCreativeReviewTab from '../../../../components/ads/admin-creative-review';
import { hasStrictPermission, PERMISSION } from '../../../../utils/admin-permissions';
import { Check, ShieldCheck, LoaderCircle, TriangleAlert } from 'lucide-react';
import {
  AdminButton,
  AdminCard,
  AdminPageHeader,
  AdminPillTabs,
  AdminSectionTitle,
  AdminStatusPill,
} from '../../ui';
import AdminEmpty from '../../components/AdminEmpty';
import KybBlacklistWarning from '../../../../components/brand-sanction/kyb-blacklist-warning';

const NoAccess: React.FC = () => (
  <div className="p-6 text-gray-500 dark:text-gray-400">Bạn không có quyền truy cập khu vực này.</div>
);

const TABS = [
  { key: 'Pending', label: 'Chờ duyệt' },
  { key: 'business', label: 'Tài khoản DN' },
  { key: 'topups', label: 'Nạp tiền DN' },
  { key: 'reported', label: 'Bị báo cáo' },
  { key: 'creatives', label: 'Nội dung thay mới' },
  { key: 'Active', label: 'Đang chạy' },
  { key: 'Rejected', label: 'Bị từ chối' },
  { key: 'Completed', label: 'Hết ngân sách' },
  { key: '', label: 'Tất cả' },
];

const ST = {
  name: { fontSize: 15, fontWeight: 600, color: 'var(--adm-heading)' } as React.CSSProperties,
  meta: { fontSize: 13, color: 'var(--adm-muted)' } as React.CSSProperties,
};

const REASON_VI: Record<string, string> = {
  Spam: 'Spam',
  Scam: 'Lừa đảo',
  Offensive: 'Phản cảm',
  Misleading: 'Sai sự thật',
  Other: 'Khác',
};

const ManagementAdsInner: React.FC = () => {
  const [tab, setTab] = useState('Pending');
  const [list, setList] = useState<AdCampaign[]>([]);
  const [reported, setReported] = useState<ReportedAd[]>([]);
  const [bizAccounts, setBizAccounts] = useState<AdAccount[]>([]);
  const [verifiedBiz, setVerifiedBiz] = useState<AdAccount[]>([]); // để cấp hạn mức tín dụng (E5)
  const [topUps, setTopUps] = useState<TopUp[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async (status: string) => {
    try {
      setLoading(true);
      if (status === 'reported') {
        setReported(await getReportedAds());
      } else if (status === 'business') {
        const [pending, verified] = await Promise.all([
          adminListBusinessAccounts('Pending'),
          adminListBusinessAccounts('Verified'),
        ]);
        setBizAccounts(pending);
        setVerifiedBiz(verified);
      } else if (status === 'creatives') {
        // thân tab tự tải dữ liệu
      } else if (status === 'topups') {
        setTopUps(await adminListPendingTopUps());
      } else {
        setList(await adminListAdCampaigns(status || undefined));
      }
    } catch {
      toast.error('Không tải được danh sách quảng cáo.');
    } finally {
      setLoading(false);
    }
  };

  const reviewTopUp = async (topUpId: string, confirm: boolean, nameMismatch?: boolean) => {
    let note: string | undefined;
    if (!confirm) {
      const r = window.prompt('Lý do từ chối yêu cầu nạp:', 'Chưa nhận được tiền');
      if (r === null) return;
      note = r;
    }
    // Chống mạo danh: nếu tên chủ TK chuyển tiền lệch tên công ty, bắt admin xác nhận đè có chủ đích.
    let force = false;
    if (confirm && nameMismatch) {
      if (!window.confirm('⚠ Tên tài khoản chuyển tiền KHÔNG khớp tên công ty. Bạn đã đối chiếu sao kê và chắc chắn đúng công ty? Bấm OK để xác nhận đè.'))
        return;
      force = true;
    }
    try {
      setBusy(topUpId);
      await adminReviewTopUp(topUpId, confirm, note, force);
      toast.success(confirm ? 'Đã xác nhận + cộng ví doanh nghiệp.' : 'Đã từ chối yêu cầu nạp.');
      load('topups');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thao tác thất bại.');
    } finally {
      setBusy(null);
    }
  };

  const reviewBiz = async (accountId: string, approve: boolean) => {
    let reason: string | undefined;
    if (!approve) {
      const r = window.prompt('Lý do từ chối hồ sơ doanh nghiệp:', 'Giấy tờ không hợp lệ');
      if (r === null) return;
      reason = r;
    }
    try {
      setBusy(accountId);
      await adminReviewBusinessAccount(accountId, approve, reason);
      toast.success(approve ? 'Đã xác thực doanh nghiệp.' : 'Đã từ chối hồ sơ.');
      load('business');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thao tác thất bại.');
    } finally {
      setBusy(null);
    }
  };

  // E5 — cấp/điều chỉnh hạn mức tín dụng (trả sau) cho tài khoản DN đã xác thực.
  const setCredit = async (a: AdAccount) => {
    const r = window.prompt(
      `Hạn mức tín dụng (trả sau) cho "${a.businessName}" — đơn vị VND (0 = không cấp):`,
      String(a.creditLimitVnd ?? 0)
    );
    if (r === null) return;
    const val = Number(r.replace(/[^\d]/g, ''));
    if (Number.isNaN(val) || val < 0) return toast.error('Hạn mức không hợp lệ.');
    try {
      setBusy(a.id);
      await adminSetAdAccountCredit(a.id, val);
      toast.success(`Đã đặt hạn mức tín dụng ${val.toLocaleString('vi-VN')}đ cho ${a.businessName}.`);
      load('business');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thao tác thất bại.');
    } finally {
      setBusy(null);
    }
  };
  useEffect(() => {
    load(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const dismissReport = async (campaignId: string) => {
    try {
      setBusy(campaignId);
      await resolveAdReport(campaignId, 'Dismiss');
      toast.success('Đã bỏ qua báo cáo — quảng cáo chạy lại.');
      load('reported');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Thao tác thất bại.');
    } finally {
      setBusy(null);
    }
  };

  const rejectReport = async (campaignId: string) => {
    const reason = window.prompt('Lý do từ chối (sẽ hoàn Ca nạp cho advertiser):', 'Vi phạm chính sách');
    if (reason === null) return;
    try {
      setBusy(campaignId);
      await resolveAdReport(campaignId, 'Reject', reason);
      toast.success('Đã từ chối + hoàn Ca nạp.');
      load('reported');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Từ chối thất bại.');
    } finally {
      setBusy(null);
    }
  };

  const approve = async (c: AdCampaign) => {
    try {
      setBusy(c.id);
      await adminReviewAdCampaign(c.id, true);
      toast.success('Đã duyệt — chiến dịch bắt đầu chạy.');
      load(tab);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Duyệt thất bại.');
    } finally {
      setBusy(null);
    }
  };

  const reject = async (c: AdCampaign) => {
    const reason = window.prompt('Lý do từ chối (sẽ hoàn Ca nạp cho advertiser):', '');
    if (reason === null) return;
    try {
      setBusy(c.id);
      await adminReviewAdCampaign(c.id, false, reason);
      toast.success('Đã từ chối + hoàn Ca nạp.');
      load(tab);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Từ chối thất bại.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="adm-page">
      <AdminPageHeader title="Quảng cáo — Cab Ads" description="Duyệt chiến dịch, tài khoản và nạp tiền của doanh nghiệp chạy Cab Ads." />

      <AdminPillTabs
        ariaLabel="Lọc mục quảng cáo"
        items={TABS.map((t) => ({ key: t.key, label: t.label }))}
        value={tab}
        onChange={setTab}
      />

      {tab === 'creatives' ? (
        <AdminCreativeReviewTab />
      ) : loading ? (
        <AdminEmpty icon={<LoaderCircle size={22} className="adm-btn__spin" />}>Đang tải…</AdminEmpty>
      ) : tab === 'business' ? (
        bizAccounts.length === 0 && verifiedBiz.length === 0 ? (
          <AdminEmpty>Chưa có hồ sơ doanh nghiệp nào.</AdminEmpty>
        ) : (
          <div className="adm-stack">
            {bizAccounts.length > 0 && <AdminSectionTitle title="Hồ sơ chờ duyệt KYB" />}
            {bizAccounts.map((a) => (
              <AdminCard key={a.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 adm-stack" style={{ gap: 6 }}>
                    <div style={ST.name}>{a.businessName}</div>
                    <div style={ST.meta}>
                      MST: {a.taxCode}
                      {a.businessLicenseNo ? ` · GPKD: ${a.businessLicenseNo}` : ''}
                      {a.representativeName ? ` · ĐD: ${a.representativeName}` : ''}
                    </div>
                    {/* Đối chiếu MST tự động (VietQR) — hỗ trợ admin duyệt đúng */}
                    {a.taxVerifiedName ? (
                      <div>
                        <AdminStatusPill
                          tone={a.taxMatch ? 'success' : 'danger'}
                          icon={a.taxMatch ? <Check size={12} /> : <TriangleAlert size={12} />}
                        >
                          {a.taxMatch ? 'Khớp thuế' : 'Lệch tên'}
                        </AdminStatusPill>{' '}
                        <span style={ST.meta}>
                          theo thuế = <b>{a.taxVerifiedName}</b>
                          {a.taxVerifiedAddress ? ` · ${a.taxVerifiedAddress}` : ''}
                        </span>
                      </div>
                    ) : (
                      <div style={ST.meta}>Chưa đối chiếu tự động — kiểm tra ảnh giấy tờ thủ công.</div>
                    )}
                    {/* Định danh người đại diện (eKYC) — chống mạo danh */}
                    <div>
                      {a.ownerIdentityVerified ? (
                        <AdminStatusPill tone="success" icon={<ShieldCheck size={12} />}>
                          Người đại diện đã eKYC{a.ownerIdentityName ? `: ${a.ownerIdentityName}` : ''}
                        </AdminStatusPill>
                      ) : (
                        <AdminStatusPill tone="danger" icon={<TriangleAlert size={12} />}>
                          Người đại diện CHƯA eKYC
                        </AdminStatusPill>
                      )}
                    </div>
                    <div style={ST.meta}>
                      {a.address || ''} {a.contactEmail ? `· ${a.contactEmail}` : ''} {a.contactPhone ? `· ${a.contactPhone}` : ''}
                    </div>
                    <KybBlacklistWarning hit={a.blacklistHit} />
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <AdminButton
                      size={32}
                      variant="danger"
                      disabled={busy === a.id}
                      onClick={() => reviewBiz(a.id, false)}
                    >
                      Từ chối
                    </AdminButton>
                    <AdminButton
                      size={32}
                      variant="success"
                      disabled={busy === a.id || !!a.blacklistHit}
                      title={a.blacklistHit ? 'Hồ sơ trùng danh sách đen, không duyệt được.' : undefined}
                      onClick={() => reviewBiz(a.id, true)}
                    >
                      Xác thực
                    </AdminButton>
                  </div>
                </div>
                {/* Giấy tờ */}
                <div className="mt-3 flex flex-wrap gap-3">
                  {[
                    { url: a.licenseImageUrl, label: 'Giấy phép KD' },
                    { url: a.taxImageUrl, label: 'Xác nhận MST' },
                    { url: a.authorizationImageUrl, label: 'Giấy ủy quyền' },
                  ]
                    .filter((d) => d.url)
                    .map((d) => (
                      <a key={d.label} href={safeHref(d.url as string)} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={d.url as string} alt={d.label} className="h-24 w-32 object-cover" style={{ borderRadius: 12, border: '1px solid var(--adm-line)' }} />
                        <span style={ST.meta}>{d.label}</span>
                      </a>
                    ))}
                </div>
              </AdminCard>
            ))}

            {/* Tài khoản DN đã xác thực — cấp hạn mức tín dụng (trả sau) — E5 */}
            {verifiedBiz.length > 0 && (
              <>
                <AdminSectionTitle title="Đã xác thực — hạn mức tín dụng (trả sau)" />
                {verifiedBiz.map((a) => {
                  const debt = Math.max(0, -(a.balanceVnd ?? 0));
                  return (
                    <AdminCard key={a.id}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0" style={{ display: 'grid', gap: 4 }}>
                          <div style={ST.name}>{a.businessName}</div>
                          <div style={ST.meta}>
                            MST: {a.taxCode} · Số dư ví:{' '}
                            <b style={(a.balanceVnd ?? 0) < 0 ? { color: 'var(--adm-danger)' } : undefined}>
                              {(a.balanceVnd ?? 0).toLocaleString('vi-VN')}đ
                            </b>
                            {debt > 0 ? ` · Đang nợ: ${debt.toLocaleString('vi-VN')}đ` : ''}
                            {' · Hạn mức: '}
                            <b>{(a.creditLimitVnd ?? 0).toLocaleString('vi-VN')}đ</b>
                          </div>
                        </div>
                        <AdminButton size={32} variant="soft" disabled={busy === a.id} onClick={() => setCredit(a)}>
                          {(a.creditLimitVnd ?? 0) > 0 ? 'Sửa hạn mức' : 'Cấp hạn mức'}
                        </AdminButton>
                      </div>
                    </AdminCard>
                  );
                })}
              </>
            )}
          </div>
        )
      ) : tab === 'topups' ? (
        topUps.length === 0 ? (
          <AdminEmpty>Không có yêu cầu nạp nào chờ đối soát.</AdminEmpty>
        ) : (
          <div className="adm-stack">
            {topUps.map((t) => (
              <AdminCard key={t.id}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0" style={{ display: 'grid', gap: 4 }}>
                    <div style={ST.name}>
                      {t.businessName} · <span style={{ color: 'var(--adm-warning)' }}>{t.amountVnd.toLocaleString('vi-VN')}đ</span>
                    </div>
                    <div style={ST.meta}>
                      Nội dung CK: <b style={{ color: 'var(--adm-danger)' }}>{t.transferNote}</b> · {new Date(t.createdAt).toLocaleString('vi-VN')}
                    </div>
                    {/* Chống mạo danh: tên chủ TK chuyển tiền + cờ khớp tên công ty */}
                    {t.payerAccountName ? (
                      <div style={ST.meta}>
                        <span>
                          Chuyển từ: <b>{t.payerAccountName}</b>
                          {t.payerAccountNo ? ` · ${t.payerAccountNo}` : ''}{t.payerBankName ? ` · ${t.payerBankName}` : ''}
                        </span>{' '}
                        <AdminStatusPill
                          tone={t.payerNameMatch ? 'success' : 'danger'}
                          icon={t.payerNameMatch ? <Check size={12} /> : <TriangleAlert size={12} />}
                        >
                          {t.payerNameMatch ? 'Khớp tên công ty' : 'Lệch tên công ty'}
                        </AdminStatusPill>
                      </div>
                    ) : (
                      <div style={ST.meta}>Chưa khai tài khoản chuyển tiền</div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <AdminButton size={32} variant="danger" disabled={busy === t.id} onClick={() => reviewTopUp(t.id, false, false)}>
                      Từ chối
                    </AdminButton>
                    <AdminButton size={32} variant="success" disabled={busy === t.id} onClick={() => reviewTopUp(t.id, true, t.payerNameMatch === false)}>
                      Đã nhận tiền — Cộng ví
                    </AdminButton>
                  </div>
                </div>
              </AdminCard>
            ))}
          </div>
        )
      ) : tab === 'reported' ? (
        reported.length === 0 ? (
          <AdminEmpty>Không có quảng cáo nào bị báo cáo.</AdminEmpty>
        ) : (
          <div className="adm-stack">
            {reported.map((r) => (
              <AdminCard key={r.campaignId}>
                <div className="flex items-center gap-3">
                  {r.creative?.imageUrl ? (
                    <img src={r.creative.imageUrl} alt="" className="w-16 h-16 object-cover shrink-0" style={{ borderRadius: 12 }} />
                  ) : (
                    <div className="w-16 h-16 shrink-0" style={{ borderRadius: 12, background: 'var(--adm-fill)' }} />
                  )}
                  <div className="flex-1 min-w-0" style={{ display: 'grid', gap: 4 }}>
                    <div style={ST.name} className="truncate">
                      {r.name}{' '}
                      <span style={{ color: 'var(--adm-danger)', fontSize: 13 }}>· {r.reportCount} báo cáo</span>
                    </div>
                    <div style={ST.meta}>
                      {Object.entries(r.reasonBreakdown)
                        .map(([k, v]) => `${REASON_VI[k] || k}: ${v}`)
                        .join(' · ')}
                    </div>
                    <div style={ST.meta}>
                      Trạng thái: {r.status} · còn {r.remainingCa} Ca · advertiser {r.advertiserId.slice(0, 8)}…
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <AdminButton size={32} variant="fill" disabled={busy === r.campaignId} onClick={() => dismissReport(r.campaignId)}>
                      Bỏ qua
                    </AdminButton>
                    <AdminButton size={32} variant="danger" disabled={busy === r.campaignId} onClick={() => rejectReport(r.campaignId)}>
                      Từ chối + hoàn Ca
                    </AdminButton>
                  </div>
                </div>
              </AdminCard>
            ))}
          </div>
        )
      ) : list.length === 0 ? (
        <AdminEmpty>Không có chiến dịch nào.</AdminEmpty>
      ) : (
        <div className="adm-stack">
          {list.map((c) => (
            <AdminCard key={c.id}>
              <div className="flex items-center gap-3">
                {c.creative?.imageUrl ? (
                  <img src={c.creative.imageUrl} alt="" className="w-16 h-16 object-cover shrink-0" style={{ borderRadius: 12 }} />
                ) : (
                  <div className="w-16 h-16 shrink-0" style={{ borderRadius: 12, background: 'var(--adm-fill)' }} />
                )}
                <div className="flex-1 min-w-0" style={{ display: 'grid', gap: 4 }}>
                  <div style={ST.name} className="truncate">{c.name}</div>
                  <div style={ST.meta}>
                    {c.format} · {c.pricingModel} · thầu {c.bidCa} Ca · ngân sách {c.totalBudgetCa} Ca
                    {c.dailyBudgetCa ? ` · ngày ${c.dailyBudgetCa} Ca` : ''}
                  </div>
                  {c.creative?.title ? (
                    <div style={{ fontSize: 14, color: 'var(--adm-text)' }} className="truncate">“{c.creative.title}” · {c.creative.landingUrl}</div>
                  ) : null}
                  <div style={ST.meta}>
                    Trạng thái: {c.status}
                    {c.rejectReason ? ` · Lý do: ${c.rejectReason}` : ''} · advertiser {c.advertiserId.slice(0, 8)}…
                  </div>
                </div>
                {c.status === 'Pending' && (
                  <div className="flex gap-2 shrink-0">
                    <AdminButton size={32} variant="danger" disabled={busy === c.id} onClick={() => reject(c)}>
                      Từ chối
                    </AdminButton>
                    <AdminButton size={32} variant="success" disabled={busy === c.id} onClick={() => approve(c)}>
                      Duyệt
                    </AdminButton>
                  </div>
                )}
              </div>
            </AdminCard>
          ))}
        </div>
      )}
    </div>
  );
};

const ManagementAds: React.FC = () =>
  hasStrictPermission(PERMISSION.AdsManage) ? <ManagementAdsInner /> : <NoAccess />;

export default ManagementAds;
